"use client";

import { motion, useReducedMotion } from "framer-motion";
import { useId, type MouseEventHandler } from "react";
import { useTranslation } from "react-i18next";
import Icon from "@/components/library/Icon";

type GailLauncherTipProps = {
  /** Distance from the viewport bottom to the tip's bottom edge, not counting the arrow. */
  bottom?: number;
  placement?: "corner" | "topbar";
  onDismiss: () => void;
  onTry: MouseEventHandler<HTMLButtonElement>;
};

const buttonClassName =
  "inline-flex min-h-9 items-center justify-center gap-2 rounded-lg px-3 text-[13px] font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-button_primary focus-visible:ring-offset-2 focus-visible:ring-offset-panel";

/** One-time note beside the launcher that Gail can read and change the page you are on. */
export default function GailLauncherTip({ bottom = 0, placement = "corner", onDismiss, onTry }: GailLauncherTipProps) {
  const { t } = useTranslation("ask");
  const reducedMotion = useReducedMotion();
  const titleId = useId();
  const bodyId = useId();

  return (
    <motion.section
      aria-labelledby={titleId}
      aria-describedby={bodyId}
      initial={{ opacity: 0, y: reducedMotion ? 0 : 4 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: reducedMotion ? 0 : 4 }}
      transition={{ duration: 0.16, ease: "easeOut" }}
      style={placement === "corner" ? { bottom } : undefined}
      onKeyDown={(event) => {
        if (event.key !== "Escape") return;
        event.preventDefault();
        onDismiss();
      }}
      className={`${placement === "topbar" ? "absolute right-0 top-full mt-3" : "fixed right-6"} z-40 flex w-[300px] max-w-[calc(100vw-3rem)] flex-col rounded-2xl border border-neutral bg-panel p-5 text-900 shadow-[0_8px_32px_rgb(15_23_42/0.1),0_2px_6px_rgb(15_23_42/0.04)]`}
    >
      <h2 id={titleId} className="text-[14px] font-semibold leading-5 tracking-[-0.01em]">
        {t("launcher.tip.title")}
      </h2>
      <p id={bodyId} className="mt-2 text-[13px] leading-5 text-text_secondary">
        {t("launcher.tip.body")}
      </p>
      <div className="mt-4 flex flex-wrap justify-end gap-2">
        <button type="button" onClick={onDismiss} className={`${buttonClassName} text-text_secondary hover:bg-200/50 hover:text-900`}>
          {t("launcher.tip.dismiss")}
        </button>
        <button type="button" onClick={onTry} className={`${buttonClassName} bg-button_primary text-button_text_primary shadow-sm hover:bg-button_primary/90 active:bg-button_primary/80`}>
          {t("launcher.tip.try")}
          <Icon icon="arrow-right" className="h-3 w-3" />
        </button>
      </div>
      <span aria-hidden className={`absolute right-5 h-2 w-2 rotate-45 border-neutral bg-panel ${placement === "topbar" ? "-top-[5px] border-t border-l" : "-bottom-[5px] border-b border-r"}`} />
    </motion.section>
  );
}
